/**
 * LA CARTE ENTREPRISE DE LA FICHE CLIENT — l'interrupteur « Client professionnel » en tête du bloc qu'il ouvre, les quatre
 * champs guidés (raison sociale, SIRET, n° de TVA, code NAF) et l'aperçu « Sur la facture ».
 *
 * ⚠️ RIEN NE BLOQUE L'ENREGISTREMENT (décision utilisateur) : une facture se corrige par avoir, et un client étranger ou un
 *    cas particulier ne doit pas rester sans fiche. Mais un identifiant faux partirait sur des documents officiels : chaque
 *    champ le SIGNALE clairement, et l'enregistrement demande confirmation (`companyWarnings`).
 * ⚠️ LES CONTRÔLES SONT CEUX DU CONTRAT (`siretCheck`, `vatCheck`, `nafLabel`) — la Console les emploie déjà : jamais une
 *    seconde clé de Luhn ici.
 * ⚠️ L'APERÇU LIT LA RÈGLE D'IMPRESSION (`invoiceClientOf` → `printedClientBlock`, `@orgazone/tickets`), celle de la caisse :
 *    il montre la personne que la facture imprime, et pas le NAF qu'elle n'imprime pas. Redessiné ici, il annoncerait une
 *    facture qui n'existe pas.
 */
import { formatSiren, siretCheck, siretDigits, normalizeVatNumber, vatCheck } from '@orgazone/sync-contract';
import { nafLabel } from '@orgazone/sync-contract/naf';
import { invoiceClientOf, printedClientBlock, printedClientLines, type PrintedClientLine } from '@orgazone/tickets';
import { useId } from 'react';
import { Icon, type IconName } from '@orgazone/ui-web';
import { t } from '../i18n';

/** Ce que la carte lit de la fiche en cours de saisie. */
export interface CompanyDraft {
  isPro: boolean;
  companyLegalName: string;
  siret: string;
  vatNumber: string;
  naf: string;
  lastName: string;
  firstName: string;
  address1: string;
  postalCode: string;
  city: string;
}

/** Le ton d'une ligne d'état : il dit si la saisie va, guide, alerte ou est fausse. */
type Tone = 'mute' | 'ok' | 'warn' | 'bad';

interface StatusLine { tone: Tone; text: string }

const TONE_COLOR: Record<Tone, string> = {
  mute: 'var(--ink-500)', ok: 'var(--teal-800)', warn: 'var(--amber-fg)', bad: 'var(--rose-fg)',
};
const TONE_ICON: Record<Tone, IconName> = { mute: 'info', ok: 'check', warn: 'alert', bad: 'alert' };

/** Un SIRET en cours de frappe, groupé comme on le lit (3 · 3 · 3 · 5) — même incomplet. */
function groupSiret(digits: string): string {
  return [digits.slice(0, 3), digits.slice(3, 6), digits.slice(6, 9), digits.slice(9, 14)].filter(Boolean).join(' ');
}

/** Ce que dit le SIRET saisi. */
export function siretStatus(siret: string): StatusLine {
  const c = siretCheck(siret);
  switch (c.state) {
    case 'empty': return { tone: 'mute', text: t('14 chiffres — sur l’avis de situation INSEE ou l’extrait Kbis') };
    case 'partial': return { tone: 'mute', text: t('{n} chiffre sur 14', { n: c.digits }) };
    case 'valid': return { tone: 'ok', text: t('SIRET valide · SIREN {siren}', { siren: formatSiren(c.siren) }) };
    case 'invalid': return { tone: 'bad', text: t('Clé de contrôle incorrecte — un chiffre est faux ou inversé') };
  }
}

/** Ce que dit le n° de TVA saisi, au regard du SIRET de la fiche. */
export function vatStatus(vat: string, siret: string): StatusLine {
  const c = vatCheck(vat, siret);
  switch (c.state) {
    case 'empty': return c.expected
      ? { tone: 'mute', text: t('Calculable depuis le SIRET : {numero}', { numero: c.expected }) }
      : { tone: 'mute', text: t('FR + clé + SIREN — proposé dès que le SIRET est complet') };
    case 'valid': return { tone: 'ok', text: t('N° de TVA intracommunautaire valide') };
    case 'mismatch': return { tone: 'warn', text: t('Ne correspond pas au SIRET — attendu {numero}', { numero: c.expected }) };
    case 'unverified': return { tone: 'mute', text: t('Clé en lettres — format accepté, non vérifiée') };
    case 'foreign': return { tone: 'mute', text: t('Numéro d’un autre pays de l’UE — format accepté, non vérifié') };
    case 'bad_key': return { tone: 'bad', text: t('Clé de contrôle incorrecte — un chiffre est faux ou inversé') };
    case 'invalid': return { tone: 'bad', text: t('Format attendu : FR, 2 caractères, puis les 9 chiffres du SIREN') };
  }
}

/** Ce que dit le code NAF saisi — son libellé INSEE et sa nomenclature quand il en est un. */
export function nafStatus(naf: string): StatusLine {
  const typed = naf.trim();
  if (!typed) return { tone: 'mute', text: t('Code APE : 4 chiffres et une lettre — ex. 47.71Z') };
  if (!/^\d{2}\.?\d{2}[A-Za-z]$/.test(typed.replace(/\s+/g, ''))) {
    return { tone: 'bad', text: t('Format attendu : 2 chiffres, 2 chiffres, une lettre — ex. 47.71Z') };
  }
  const known = nafLabel(typed);
  // ⚠️ LE LIBELLÉ EST CELUI DE L'INSEE, jamais traduit : c'est la nomenclature française officielle.
  if (known) return { tone: 'ok', text: `${known.label} · ${known.revision === 'naf2025' ? t('NAF 2025') : t('NAF rév. 2')}` };
  return { tone: 'warn', text: t('Code inconnu des nomenclatures NAF de l’INSEE') };
}

/**
 * CE QUI PARTIRAIT FAUX SUR LES FACTURES — un message par identifiant faux, rien quand tout va ou que la fiche n'est pas
 * professionnelle. L'enregistrement les présente avant d'écrire : signaler sans bloquer.
 */
export function companyWarnings(d: CompanyDraft): string[] {
  if (!d.isPro) return [];
  const out: string[] = [];
  const s = siretCheck(d.siret).state;
  if (s === 'partial' || s === 'invalid') out.push(t('SIRET : {etat}', { etat: siretStatus(d.siret).text }));
  const v = vatCheck(d.vatNumber, d.siret).state;
  if (v === 'mismatch' || v === 'bad_key' || v === 'invalid') out.push(t('N° de TVA : {etat}', { etat: vatStatus(d.vatNumber, d.siret).text }));
  const n = nafStatus(d.naf).tone;
  if (n === 'bad' || n === 'warn') out.push(t('Code NAF : {etat}', { etat: nafStatus(d.naf).text }));
  return out;
}

/** Une ligne de l'aperçu : imprimée, ou la place grisée de ce qui manque encore. */
export interface PreviewLine extends PrintedClientLine { missing: boolean }

/**
 * L'APERÇU « SUR LA FACTURE » — les lignes que la facture imprime, et en gris la place de ce qui manque encore (raison
 * sociale, adresse, SIRET, n° de TVA). Ce qui manque ne s'imprimera pas : la place grisée le montre sans l'inventer.
 */
export function invoicePreview(lines: readonly PrintedClientLine[]): PreviewLine[] {
  const has = (kind: PrintedClientLine['kind']) => lines.some((l) => l.kind === kind);
  const printed = (kind: PrintedClientLine['kind']) => lines.filter((l) => l.kind === kind).map((l) => ({ ...l, missing: false }));
  const gap = (kind: PrintedClientLine['kind'], text: string, strong = false): PreviewLine[] => (has(kind) ? [] : [{ kind, text, strong, missing: true }]);
  return [
    ...printed('company'), ...gap('company', t('Raison sociale'), true),
    ...printed('person'),
    ...printed('address'), ...gap('address', t('Adresse de la fiche')),
    ...printed('siret'), ...gap('siret', t('SIRET non renseigné')),
    ...printed('vat'), ...gap('vat', t('N° de TVA non renseigné')),
  ];
}

/** La ligne d'état sous un champ : icône et texte au ton de ce qu'elle dit. */
function Status({ line, id }: { line: StatusLine; id: string }) {
  return (
    <span id={id} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginTop: 6, fontSize: '11.5px', fontWeight: 600, color: TONE_COLOR[line.tone], lineHeight: 1.4 }}>
      <Icon name={TONE_ICON[line.tone]} size={13} stroke={2.2} style={{ flexShrink: 0, marginTop: 1 }} />
      <span>{line.text}</span>
    </span>
  );
}

const LABEL: React.CSSProperties = { display: 'block', fontSize: '11px', fontWeight: 600, color: 'var(--ink-500)', marginBottom: 5 };
const INPUT: React.CSSProperties = {
  width: '100%', height: 38, padding: '0 12px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)',
  background: 'var(--paper)', fontSize: '13px', color: 'var(--ink-900)', boxSizing: 'border-box',
};
const MONO_INPUT: React.CSSProperties = { ...INPUT, fontFamily: 'var(--font-mono)', letterSpacing: '.02em' };

/** La carte Entreprise — `onChange` reçoit un morceau de la fiche à fusionner. */
export function CompanyCard({ d, disabled, onChange }: {
  d: CompanyDraft;
  disabled: boolean;
  onChange: (patch: Partial<CompanyDraft>) => void;
}) {
  /** Le préfixe des identifiants qui relient chaque champ à sa ligne d'état. */
  const ids = useId();
  const vat = vatCheck(d.vatNumber, d.siret);
  const offer = vat.expected && normalizeVatNumber(d.vatNumber) !== vat.expected && !disabled ? vat.expected : null;
  const printed = printedClientBlock(invoiceClientOf({
    lastName: d.lastName, firstName: d.firstName || null, isPro: true, companyLegalName: d.companyLegalName || null,
    address1: d.address1 || null, address2: null, postalCode: d.postalCode || null, city: d.city || null, country: null,
    siret: d.siret || null, vatNumber: d.vatNumber || null,
  }));
  const preview = invoicePreview(printedClientLines(printed));
  const ink = 'var(--ink-700)';
  const missing = 'var(--ink-300)';
  const toggle = () => { if (!disabled) onChange({ isPro: !d.isPro }); };

  return (
    <div style={{ border: `1px solid ${d.isPro ? 'var(--teal-400)' : 'var(--ink-200)'}`, borderRadius: 'var(--r-md)', background: 'var(--paper)', overflow: 'hidden', transition: 'border-color .15s' }}>
      {/* ⚠️ L'EN-TÊTE ENTIER EST L'INTERRUPTEUR — un seul bouton `role="switch"`, nommé par son libellé : la piste à droite
          n'est que son dessin. Géométrie de la charte (38 × 22), la même que tous les interrupteurs du back-office. */}
      <button type="button" role="switch" aria-checked={d.isPro} disabled={disabled} onClick={toggle}
        style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '12px 14px', border: 'none', background: d.isPro ? 'var(--teal-50)' : 'var(--paper)', textAlign: 'left', cursor: disabled ? 'not-allowed' : 'pointer', transition: 'background .15s' }}>
        <span style={{ width: 36, height: 36, borderRadius: 'var(--r-sm)', background: 'var(--blue-bg)', color: 'var(--blue-fg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon name="building" size={17} stroke={1.9} />
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: '14px', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Client professionnel')}</span>
          <span style={{ display: 'block', marginTop: 2, fontSize: '12px', color: 'var(--ink-600)', lineHeight: 1.4 }}>
            {d.isPro
              ? t('Ses factures portent la raison sociale, le SIRET et le n° de TVA.')
              : t('Activez pour facturer à une entreprise : raison sociale, SIRET, TVA et code NAF.')}
          </span>
        </span>
        <span aria-hidden style={{ position: 'relative', width: 38, height: 22, borderRadius: 'var(--r-pill)', background: d.isPro ? 'var(--teal-500)' : 'var(--ink-200)', flexShrink: 0, transition: 'background .15s' }}>
          <span style={{ position: 'absolute', top: 3, left: d.isPro ? 19 : 3, width: 16, height: 16, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,.28)', transition: 'left .15s' }} />
        </span>
      </button>

      {d.isPro && (
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start', padding: 14, borderTop: '1px solid var(--ink-100)' }}>
          <div style={{ flex: '1 1 300px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <label style={{ display: 'block', minWidth: 0 }}>
              <span style={LABEL}>{t('Raison sociale')}</span>
              <input value={d.companyLegalName} onChange={(e) => onChange({ companyLegalName: e.target.value })} disabled={disabled}
                autoComplete="off" spellCheck={false} placeholder={t('Ex. SARL DUVAL & FILS')}
                style={{ ...INPUT, fontSize: '13.5px', fontWeight: 600 }} />
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: 12, alignItems: 'start' }}>
              <div style={{ minWidth: 0 }}>
                <label style={{ display: 'block', minWidth: 0 }}>
                  <span style={LABEL}>SIRET</span>
                  <input value={groupSiret(siretDigits(d.siret))} onChange={(e) => onChange({ siret: siretDigits(e.target.value) })} disabled={disabled}
                    autoComplete="off" spellCheck={false} inputMode="numeric" placeholder="123 456 789 00012" aria-describedby={`${ids}-siret`} style={MONO_INPUT} />
                </label>
                <Status id={`${ids}-siret`} line={siretStatus(d.siret)} />
              </div>
              <div style={{ minWidth: 0 }}>
                <label style={{ display: 'block', minWidth: 0 }}>
                  <span style={LABEL}>{t('N° de TVA intracommunautaire')}</span>
                  <input value={d.vatNumber} onChange={(e) => onChange({ vatNumber: normalizeVatNumber(e.target.value).slice(0, 14) })} disabled={disabled}
                    autoComplete="off" spellCheck={false} placeholder={t('FR 00 000000000')} aria-describedby={`${ids}-vat`} style={{ ...MONO_INPUT, textTransform: 'uppercase' }} />
                </label>
                <Status id={`${ids}-vat`} line={vatStatus(d.vatNumber, d.siret)} />
                {offer && (
                  <button type="button" onClick={() => onChange({ vatNumber: offer })}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 28, marginTop: 6, padding: '0 10px', border: '1px solid var(--teal-400)', borderRadius: 'var(--r-ctrl)', background: 'var(--teal-50)', color: 'var(--teal-800)', fontFamily: 'var(--font-mono)', fontSize: '11.5px', fontWeight: 700, whiteSpace: 'nowrap', cursor: 'pointer' }}>
                    <Icon name="arrowR" size={12} stroke={2.4} />
                    {t('Utiliser {numero}', { numero: offer })}
                  </button>
                )}
              </div>
            </div>
            <div style={{ maxWidth: 420 }}>
              <label style={{ display: 'block', minWidth: 0 }}>
                <span style={LABEL}>{t('Code NAF (APE)')}</span>
                <input value={d.naf} onChange={(e) => onChange({ naf: e.target.value.toUpperCase().replace(/\s/g, '').slice(0, 6) })} disabled={disabled}
                  autoComplete="off" spellCheck={false} placeholder="47.71Z" aria-describedby={`${ids}-naf`} style={{ ...MONO_INPUT, textTransform: 'uppercase', maxWidth: 160 }} />
              </label>
              <Status id={`${ids}-naf`} line={nafStatus(d.naf)} />
            </div>
          </div>

          <div style={{ flex: '1 1 240px', maxWidth: '100%', minWidth: 220, padding: 12, borderRadius: 'var(--r-sm)', background: 'var(--ink-50)', border: '1px solid var(--ink-100)', boxSizing: 'border-box' }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--ink-500)', fontWeight: 600 }}>{t('Sur la facture')}</span>
            {/* ⚠️ CE QUE LA FACTURE IMPRIME, dans son ordre (`printedClientBlock`) : la raison sociale, la personne, l'adresse,
                « SIRET … », « TVA … » — en gris, la place de ce qui manque encore. Le NAF n'y est pas : la facture ne
                l'imprime pas. Papier de facture : blanc quel que soit le thème. */}
            <div style={{ marginTop: 8, padding: '12px 13px', borderRadius: 8, background: '#ffffff', border: '1px solid #e2e6ea', boxShadow: '0 6px 16px -10px rgba(12,14,16,.3)', fontSize: '11.5px', lineHeight: 1.6, color: '#2b3034' }}>
              {preview.map((l, i) => (
                <div key={`${l.kind}-${i}`} style={{
                  color: l.missing ? missing : l.strong ? 'var(--ink-900)' : ink, wordBreak: 'break-word', fontVariantNumeric: 'tabular-nums',
                  ...(l.kind === 'company' ? { fontSize: '12.5px', fontWeight: 800, letterSpacing: '.01em' } : l.strong ? { fontWeight: 700 } : {}),
                }}>
                  {l.text}
                </div>
              ))}
            </div>
            <span style={{ display: 'block', marginTop: 8, fontSize: '11px', color: 'var(--ink-500)', lineHeight: 1.45 }}>
              {t('L’adresse vient de la fiche. Rien n’est bloquant : une facture se corrige par avoir.')}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
